import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, RotateCcw } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useDemoDialog } from "@/components/DemoRequestProvider";
import { CASE_STUDIES_HREF, FONDEA_HREF, getHomeV2 } from "@/content/home-v2";
import FondeaLogo from "@/components/fondea/FondeaLogo";
import SolvennLogo from "@/components/solvenn/SolvennLogo";

const Eyebrow = ({ children }: { children: string }): JSX.Element => (
  <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-primary">[ {children} ]</p>
);

export const CasesTeaser = (): JSX.Element => {
  const { language } = useLanguage();
  const c = getHomeV2(language).cases;
  return (
    <section className="section-padding section-band-alt">
      <div className="enterprise-container">
        <Eyebrow>{c.eyebrow}</Eyebrow>
        <h2 className="section-title mb-12 max-w-2xl text-foreground">{c.title}</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {c.items.map((item) => (
            <div key={item.body} className="rounded-2xl border border-border bg-card p-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">{item.tag}</span>
              <p className="mt-4 text-foreground">{item.body}</p>
            </div>
          ))}
        </div>
        <Link to={CASE_STUDIES_HREF} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
          {c.cta} <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
};

type Answer = boolean | undefined;

/** Interactive 10-question maturity self-assessment (client-side only, nothing stored). */
export const MaturityCheck = (): JSX.Element => {
  const { language } = useLanguage();
  const { open: openDemo } = useDemoDialog();
  const c = getHomeV2(language).maturity;
  const [answers, setAnswers] = useState<Answer[]>(() => Array(c.questions.length).fill(undefined));

  const answered = answers.filter((a) => a !== undefined).length;
  const yesCount = answers.filter((a) => a === true).length;
  const isComplete = answered === c.questions.length;
  const result = yesCount >= 8 ? c.results.high : yesCount >= 5 ? c.results.mid : c.results.low;

  const setAnswer = (index: number, value: boolean): void =>
    setAnswers((prev) => prev.map((a, i) => (i === index ? value : a)));

  return (
    <section className="section-padding section-band-base">
      <div className="enterprise-container grid gap-10 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 className="section-title mb-8 text-foreground">{c.title}</h2>
          <ol className="divide-y divide-border rounded-2xl border border-border bg-card">
            {c.questions.map((q, i) => (
              <li key={q} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-sm text-foreground">
                  <span className="mr-3 font-mono text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                  {q}
                </span>
                <div className="flex shrink-0 gap-2" role="group" aria-label={q}>
                  {[true, false].map((value) => (
                    <button
                      key={String(value)}
                      type="button"
                      aria-pressed={answers[i] === value}
                      onClick={() => setAnswer(i, value)}
                      className={`min-w-14 rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                        answers[i] === value
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {value ? c.yes : c.no}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        </div>
        <aside className="lg:sticky lg:top-32 lg:self-start rounded-3xl border border-primary/30 bg-card p-8" aria-live="polite">
          <p className="font-mono text-xs text-muted-foreground">
            {answered}/{c.questions.length} {c.progress}
          </p>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-primary transition-all" style={{ width: `${(answered / c.questions.length) * 100}%` }} />
          </div>
          {isComplete ? (
            <>
              <p className="mt-8 font-display text-5xl font-semibold text-primary">{yesCount}/10</p>
              <p className="text-xs text-muted-foreground">{c.score}</p>
              <h3 className="mt-6 font-display text-2xl font-semibold text-foreground">{result.title}</h3>
              <p className="mt-2 text-muted-foreground">{result.body}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button type="button" onClick={openDemo} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
                  {c.cta} <ArrowRight className="size-4" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => setAnswers(Array(c.questions.length).fill(undefined))}
                  className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-muted-foreground hover:text-foreground"
                >
                  <RotateCcw className="size-4" aria-hidden="true" /> {c.reset}
                </button>
              </div>
            </>
          ) : (
            <p className="mt-8 text-sm text-muted-foreground">{c.title}</p>
          )}
        </aside>
      </div>
    </section>
  );
};

export const MethodTimeline = (): JSX.Element => {
  const { language } = useLanguage();
  const c = getHomeV2(language).method;
  return (
    <section className="section-padding section-band-alt">
      <div className="enterprise-container">
        <Eyebrow>{c.eyebrow}</Eyebrow>
        <h2 className="section-title mb-12 text-foreground">{c.title}</h2>
        <ol className="grid gap-6 border-t border-border pt-8 sm:grid-cols-3 lg:grid-cols-6">
          {c.steps.map((s, i) => (
            <li key={s.title}>
              <span className="font-mono text-sm text-primary">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-2 font-semibold text-foreground">{s.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/** Final dual CTA — separate paths, never one shared form. */
export const DualCTA = (): JSX.Element => {
  const { language } = useLanguage();
  const { open: openDemo } = useDemoDialog();
  const c = getHomeV2(language).finalCta;
  return (
    <section className="section-padding section-band-base">
      <div className="enterprise-container">
        <h2 className="section-title mb-12 text-center text-foreground">{c.title}</h2>
        <div className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
          <div className="rounded-3xl border border-primary/30 bg-card p-8 md:p-10">
            <FondeaLogo className="h-9 w-auto" />
            <p className="mt-5 font-display text-2xl font-semibold text-foreground">{c.fondea.body}</p>
            <Link to={FONDEA_HREF} className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
              {c.fondea.cta} <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
          <div className="rounded-3xl border border-border bg-card/60 p-8">
            <SolvennLogo className="h-10 w-auto -ml-2" />
            <p className="mt-4 text-lg text-foreground">{c.whiteLabel.body}</p>
            <button type="button" onClick={openDemo} className="mt-8 inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground hover:bg-muted">
              {c.whiteLabel.cta} <ArrowRight className="size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
